/**
 * Canonical pieces management view.
 *
 * Shows all canonical pieces (unique musical works) with suggestion counts.
 * Allows admins to view which members suggested each piece and manage linkages.
 */

import { useState } from 'react'
import { useCanonicalPieces } from '../hooks/useCanonicalPieces'
import { Card, CardDescription, CardHeader, CardTitle } from '@brent/core-ui/ui'
import { Alert, AlertDescription } from '@brent/core-ui/ui'
import { ManagementTabs } from '../components/shared/ManagementTabs'
import { Loader2 } from 'lucide-react'
import { CanonicalPieceDetailsDialog } from '../components/canonical-pieces/CanonicalPieceDetailsDialog'
import { CanonicalPiecesTable } from '../components/canonical-pieces/CanonicalPiecesTable'
import type { CanonicalPiece } from '../types/canonical-piece'

export function CanonicalPieces() {
  const { data: canonicalPieces, isLoading, error } = useCanonicalPieces()
  const [selectedPiece, setSelectedPiece] = useState<CanonicalPiece | null>(null)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)

  const handleViewDetails = (piece: CanonicalPiece) => {
    setSelectedPiece(piece)
    setDetailsDialogOpen(true)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load canonical pieces: {error.message}</AlertDescription>
      </Alert>
    )
  }

  if (!canonicalPieces || canonicalPieces.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Canonical Pieces</CardTitle>
          <CardDescription>
            No canonical pieces yet. Canonical pieces are created automatically when suggestions are
            resolved.
          </CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <ManagementTabs />

      <p className="text-muted-foreground">
        View all unique musical works suggested by members. Each piece shows how many members have
        suggested it.
      </p>

      <CanonicalPiecesTable pieces={canonicalPieces} onViewDetails={handleViewDetails} />

      {selectedPiece && (
        <CanonicalPieceDetailsDialog
          canonicalPiece={selectedPiece}
          open={detailsDialogOpen}
          onOpenChange={setDetailsDialogOpen}
        />
      )}
    </div>
  )
}
